import { Eye, EyeOff } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useProfileVisibility } from "@/lib/safety/useProfileVisibility";
import { toast } from "sonner";

const ROWS: { key: keyof NonNullable<ReturnType<typeof useProfileVisibility>["visibility"]>; label: string; help: string }[] = [
  { key: "visible_to_unverified" as const, label: "Visible to unverified members", help: "Members who haven't completed ID verification can find your profile." },
  { key: "visible_to_other_apps" as const, label: "Visible across all three apps", help: "Show up in Fryqnc, Pulsynq, and MindLynq discovery." },
  { key: "visible_in_search" as const, label: "Appear in search", help: "Allow people to find you by username or display name." },
  { key: "hide_age_from_strangers" as const, label: "Hide age until match", help: "Your age is only revealed once you and another member match." },
  { key: "hide_distance_from_strangers" as const, label: "Hide distance until match", help: "Your approximate distance is only revealed after a match." },
  { key: "blur_photos_until_match" as const, label: "Blur photos until match", help: "Strangers see blurred photos. Matches see clear photos." },
];

const WhoCanSeeMePanel = () => {
  const { visibility, loading, update } = useProfileVisibility();

  if (loading || !visibility) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  const toggle = async (key: keyof typeof visibility, val: boolean) => {
    const { error } = await update({ [key]: val } as Partial<typeof visibility>);
    if (error) toast.error(error);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <Eye className="h-5 w-5 mt-0.5 text-muted-foreground" />
        <div className="flex-1">
          <p className="text-sm">
            Decide exactly who can see what — every toggle is enforced server-side.
          </p>
        </div>
      </div>
      <div className="rounded-2xl border border-border bg-card divide-y divide-border">
        {ROWS.map((row) => {
          const value = visibility[row.key] as boolean;
          return (
            <div key={row.key as string} className="flex items-start justify-between gap-4 px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {value ? (
                    <Eye className="h-3.5 w-3.5 text-success" />
                  ) : (
                    <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                  <Label className="text-sm font-medium">{row.label}</Label>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">{row.help}</p>
              </div>
              <Switch checked={value} onCheckedChange={(v) => toggle(row.key, v)} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WhoCanSeeMePanel;
